import { FC } from 'react';
import cn from 'clsx';

/**
 * The loading surface: a gray block with a pale band travelling across it.
 *
 * Exported as a class as well as a component because some shapes already exist. A
 * skeleton is only ever a shape wearing this surface, so a shape that is already drawn —
 * a tile's cover box, a heading's rule — takes the class and stays one declaration
 * instead of becoming a second component that has to be kept in step with the first.
 *
 * The gradient is twice the block's width and `animate-shimmer` walks it across; both
 * halves of that pairing are needed, and a `bg-[length:...]` without the animation is
 * just a gray box. `motion-reduce` leaves the gradient parked, which still reads as a
 * placeholder — a band sweeping across a dozen blocks at once is exactly the motion
 * somebody who asks for less of it is asking about.
 */
export const SKELETON_SURFACE =
  'bg-gradient-to-r from-slate-200 via-slate-50 to-slate-200 bg-[length:200%_100%] animate-shimmer motion-reduce:animate-none';

interface Props {
  className?: string;
}

/**
 * One placeholder block.
 *
 * `aria-hidden`, always: the page's loading state says what is happening once, and forty
 * blocks each announcing themselves is worse than the one sentence. Callers give it its
 * shape — a height, a width, a radius — because a skeleton has no content to take a size
 * from.
 */
const Skeleton: FC<Props> = ({ className }) => {
  return <span aria-hidden className={cn('block rounded', SKELETON_SURFACE, className)} />;
};

export default Skeleton;
